import React, { useState, useEffect } from 'react';
import { Search, Calendar, User, Package, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Transaction, TransactionType } from '@/types';
import { apiService } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const TransactionList: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<TransactionType | 'ALL'>('ALL');
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await apiService.get<Transaction[]>('/transactions');
      setTransactions(response || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Fehler beim Laden der Transaktionen');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeBadge = (type: TransactionType, item: any, status?: string) => {
    // Zeige Storniert-Badge wenn Status CANCELLED ist
    if (status === 'CANCELLED') {
      return (
        <Badge variant="outline" className="flex items-center gap-1 border-red-500 text-red-600">
          <span>❌</span>
          Storniert
        </Badge>
      );
    }

    // Dynamische Labels basierend auf Mietgerät oder Verbrauchsmaterial
    const checkoutLabel = item?.isRental ? 'Ausgeliehen' : 'Entnommen';
    const checkinLabel = item?.isRental ? 'Zurückgegeben' : 'Eingelagert';
    
    const typeConfig = {
      [TransactionType.CHECKOUT]: { variant: 'destructive' as const, label: checkoutLabel, icon: '📤' },
      [TransactionType.CHECKIN]: { variant: 'default' as const, label: checkinLabel, icon: '📥' },
      [TransactionType.CANCEL]: { variant: 'outline' as const, label: 'Storniert', icon: '❌' },
      [TransactionType.CREATE]: { variant: 'secondary' as const, label: 'Erstellt', icon: '➕' },
      [TransactionType.UPDATE]: { variant: 'secondary' as const, label: 'Aktualisiert', icon: '✏️' },
      [TransactionType.DELETE]: { variant: 'destructive' as const, label: 'Gelöscht', icon: '🗑️' },
    };

    const config = typeConfig[type];
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <span>{config.icon}</span>
        {config.label}
      </Badge>
    );
  };

  const handleCancelTransaction = async (transaction: Transaction) => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können Transaktionen stornieren');
      return;
    }

    if (transaction.status === 'CANCELLED') {
      toast.error('Diese Transaktion ist bereits storniert');
      return;
    }

    if (!confirm(`Möchten Sie die Transaktion "${transaction.item.name}" wirklich stornieren?`)) {
      return;
    }

    try {
      await apiService.post(`/transactions/${transaction.id}/cancel`);
      toast.success('Transaktion erfolgreich storniert');
      fetchTransactions(); // Refresh the list
    } catch (error: any) {
      console.error('Error canceling transaction:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Stornieren der Transaktion');
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = 
      transaction.item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'ALL' || transaction.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transaktionen ({filteredTransactions.length})</CardTitle>
              <p className="text-sm text-muted-foreground">
                Übersicht aller Ein- und Auscheckvorgänge
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Nach Transaktionen suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TransactionType | 'ALL')}
              className="px-3 py-2 border border-input rounded-md bg-background text-sm"
            >
              <option value="ALL">Alle Typen</option>
              <option value={TransactionType.CHECKOUT}>Ausgeliehen/Entnommen</option>
              <option value={TransactionType.CHECKIN}>Zurückgegeben/Eingelagert</option>
              <option value={TransactionType.CANCEL}>Storniert</option>
              <option value={TransactionType.CREATE}>Erstellt</option>
              <option value={TransactionType.UPDATE}>Aktualisiert</option>
              <option value={TransactionType.DELETE}>Gelöscht</option>
            </select>
          </div>

          {/* Transactions List */}
          <div className="space-y-4">
            {filteredTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex flex-col">
                    <div className="flex items-center space-x-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{transaction.item.name}</span>
                      {transaction.item.sku && (
                        <span className="text-sm text-muted-foreground">({transaction.item.sku})</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>{transaction.user?.name}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>
                          {format(new Date(transaction.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </span>
                      </div>
                      <span className="font-medium">Menge: {transaction.quantity}</span>
                    </div>
                    {transaction.notes && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {transaction.notes}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getTransactionTypeBadge(transaction.type, transaction.item, (transaction as any).status)}
                  {isAdmin && (transaction as any).status !== 'CANCELLED' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelTransaction(transaction)}
                      className="flex items-center gap-1 text-red-600 hover:text-red-700"
                    >
                      <X className="h-3 w-3" />
                      Stornieren
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredTransactions.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Keine Transaktionen gefunden
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransactionList;
